import { normalizeText } from './normalizeText';
import type { ConversationJobLink } from './types';

/** One conversation as the register renders it. `parentSessionId` is the conversation that DELEGATED this
 *  one; everything else is the flat row the daemon already served. */
export interface ConversationRow {
  id: string;
  title: string;
  model: string;
  updated_at: string;
  running: boolean;
  kind: 'conversation' | 'channel' | 'task';
  tokens?: number;
  ownerId?: number;
  ownerLabel?: string;
  platform?: string | null;
  direct?: boolean;
  lastWriterId?: number | null;
  lastWriterLabel?: string | null;
  parentSessionId?: string | null;
}

/** A conversation with the sessions it delegated and the recurring jobs organized under it. `depth` is
 *  the nesting level of this node (a root is 0) and `descendantCount` counts SESSIONS below it — job
 *  links are counted separately, because a schedule is not a conversation and must not enter the same
 *  identity namespace or token total.
 *
 *  The sub-agent tree is deliberately NOT a field here. It is fetched for the page of roots actually on
 *  screen, so baking it into the forest the pagination is computed from would make the page depend on a
 *  read that depends on the page. ConversationListPanel looks it up by conversation id at render time
 *  instead, using countRunningAgents for running counts and the plugin history branch for rendering. */
export interface ConversationTreeNode {
  row: ConversationRow;
  depth: number;
  children: ConversationTreeNode[];
  jobs: ConversationJobLink[];
  descendantCount: number;
}

/** Group the flat link response by conversation, ordered by job name with the job id as the tiebreaker so
 *  two identically named schedules keep a stable position across refetches. */
export function groupJobLinks(links: readonly ConversationJobLink[]): Map<string, ConversationJobLink[]> {
  const byConversation = new Map<string, ConversationJobLink[]>();
  for (const link of links) {
    const bucket = byConversation.get(link.conversationId);
    if (bucket) bucket.push(link);
    else byConversation.set(link.conversationId, [link]);
  }
  for (const bucket of byConversation.values()) {
    bucket.sort((a, b) => a.name.localeCompare(b.name) || a.jobId.localeCompare(b.jobId));
  }
  return byConversation;
}

/** Build the conversation forest out of the flat rows.
 *
 *  A child is attached only to a parent that is PRESENT in the same response and belongs to the same
 *  account: deleting a parent detaches its children rather than cascading, and an admin register must not
 *  file one person's conversation under another's. Anything else — a missing parent, a foreign parent, a
 *  row pointing at itself, a cycle — stays a root, so every input row is rendered exactly once and the
 *  recursion below is finite whatever the stored ancestry says. */
export function buildConversationTree(
  rows: readonly ConversationRow[],
  jobsByConversation: ReadonlyMap<string, ConversationJobLink[]>,
): ConversationTreeNode[] {
  const byId = new Map<string, ConversationRow>();
  for (const row of rows) if (!byId.has(row.id)) byId.set(row.id, row);

  /** The parent this row may actually hang under, or null. */
  const parentOf = (row: ConversationRow): string | null => {
    const parentId = row.parentSessionId;
    if (!parentId || parentId === row.id) return null;
    const parent = byId.get(parentId);
    if (!parent || parent.ownerId !== row.ownerId) return null;
    return parentId;
  };

  // Resolve every edge first, then break the ones that would close a loop: walking up from the parent has
  // to terminate, and it can only do so once no row is its own ancestor.
  const edges = new Map<string, string>();
  for (const row of byId.values()) {
    const parentId = parentOf(row);
    if (parentId) edges.set(row.id, parentId);
  }
  for (const id of [...edges.keys()]) {
    const seen = new Set<string>([id]);
    for (let up = edges.get(id); up; up = edges.get(up)) {
      if (seen.has(up)) { edges.delete(id); break; }
      seen.add(up);
    }
  }

  const childrenOf = new Map<string, ConversationRow[]>();
  const roots: ConversationRow[] = [];
  for (const row of byId.values()) {
    const parentId = edges.get(row.id);
    if (!parentId) { roots.push(row); continue; }
    const bucket = childrenOf.get(parentId);
    if (bucket) bucket.push(row);
    else childrenOf.set(parentId, [row]);
  }

  const build = (row: ConversationRow, depth: number): ConversationTreeNode => {
    const children = (childrenOf.get(row.id) ?? []).map((child) => build(child, depth + 1));
    return {
      row,
      depth,
      children,
      jobs: jobsByConversation.get(row.id) ?? [],
      descendantCount: children.reduce((total, child) => total + 1 + child.descendantCount, 0),
    };
  };
  return roots.map((row) => build(row, 0));
}

/** Order the roots and, independently, each sibling set — the register's column sort applies to a branch
 *  exactly as it applies to the top level. */
export function sortConversationTree(
  nodes: readonly ConversationTreeNode[],
  compare: (a: ConversationRow, b: ConversationRow) => number,
): ConversationTreeNode[] {
  return nodes
    .map((node) => ({ ...node, children: sortConversationTree(node.children, compare) }))
    .sort((a, b) => compare(a.row, b.row));
}

/** What filtering leaves standing, separately from the caller's manual expansion. */
export interface FilteredConversationTree {
  roots: ConversationTreeNode[];
  expanded: Set<string>;
  jobsExpanded: Set<string>;
  /** Direct row or schedule matches; never ancestry retained only for navigation. */
  matchedIds: Set<string>;
  /** Visible ancestors that must not enter direct-match counts or page selection. */
  contextIds: Set<string>;
}

/** Text and job matching are ANDed with row facets, never used as a substitute for them.
 *  A direct text hit retains its facet-eligible family. Nonmatching ancestors are context only.
 *  Forced expansion reveals real matches below a node without changing manual expansion. An empty
 *  text needle disables job matching, so facet-only filtering cannot match every schedule by accident.
 *  Omit matchFacets when no facet is active, so clearing all filters restores manual expansion. */
export function filterConversationTree(
  roots: readonly ConversationTreeNode[],
  needle: string,
  matchRow: (row: ConversationRow) => boolean,
  matchFacets?: (row: ConversationRow) => boolean,
): FilteredConversationTree {
  const expanded = new Set<string>();
  const jobsExpanded = new Set<string>();
  const matchedIds = new Set<string>();
  const contextIds = new Set<string>();
  const searching = needle.length > 0;
  const jobMatches = (job: ConversationJobLink): boolean => searching && normalizeText(job.name).includes(needle);

  const prune = (node: ConversationTreeNode, familyMatch: boolean): { node: ConversationTreeNode; hasMatch: boolean } | null => {
    const eligible = matchFacets ? matchFacets(node.row) : true;
    const textMatch = eligible && matchRow(node.row);
    const matchedJobs = eligible ? node.jobs.filter(jobMatches) : [];
    const selfMatch = textMatch || matchedJobs.length > 0;
    const familyVisible = eligible && familyMatch;
    if (selfMatch) matchedIds.add(node.row.id);
    const keptChildren = node.children
      .map((child) => prune(child, familyMatch || textMatch))
      .filter((child): child is NonNullable<typeof child> => child !== null);
    if (!selfMatch && !familyVisible && keptChildren.length === 0) return null;
    if (!selfMatch && !familyVisible) contextIds.add(node.row.id);
    const descendantMatch = keptChildren.some((child) => child.hasMatch);
    if (descendantMatch && (searching || matchFacets !== undefined)) expanded.add(node.row.id);
    if (matchedJobs.length > 0) { expanded.add(node.row.id); jobsExpanded.add(node.row.id); }
    const children = keptChildren.map((child) => child.node);
    return {
      hasMatch: selfMatch || descendantMatch,
      node: {
        ...node,
        children,
        jobs: textMatch || familyVisible ? node.jobs : matchedJobs,
        descendantCount: children.reduce((total, child) => total + 1 + child.descendantCount, 0),
      },
    };
  };
  return {
    roots: roots.map((root) => prune(root, false)).filter((root): root is NonNullable<typeof root> => root !== null).map((root) => root.node),
    expanded, jobsExpanded, matchedIds, contextIds,
  };
}
